import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface ProductFeature {
  name: string;
  description: string;
  can_use: boolean;
  required_tiers: string[];
  is_pro_only: boolean;
}

interface UseProductFeaturesResult {
  features: Record<string, ProductFeature>;
  loading: boolean;
  error: Error | null;
  canUseFeature: (featureKey: string) => boolean;
  isProOnly: (featureKey: string) => boolean;
  refresh: () => void;
}

/**
 * Custom hook for fetching and checking product features
 *
 * @param productKey - The product key to fetch features for (e.g., 'teaching_program', 'slideshow_generative')
 * @returns Object containing features, loading state, error, and helper functions
 *
 * @example
 * const { features, canUseFeature, isProOnly } = useProductFeatures('teaching_program');
 *
 * <MenuItem disabled={!canUseFeature('lesson_count_10')}>
 *   10 Lessons {isProOnly('lesson_count_10') && <Chip label="Pro" />}
 * </MenuItem>
 */
export const useProductFeatures = (productKey: string): UseProductFeaturesResult => {
  const [features, setFeatures] = useState<Record<string, ProductFeature>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  useEffect(() => {
    const fetchFeatures = async () => {
      setLoading(true);
      setError(null);

      try {
        const apiUrl = process.env.REACT_APP_API_SERVER_URL || '';
        const token = await getAccessTokenSilently();
        const response = await fetch(`${apiUrl}/api/products/${productKey}/features`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch features: ${response.statusText}`);
        }

        const data = await response.json();
        setFeatures(data.features || {});
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error fetching features');
        setError(error);
        console.error(`Error fetching features for product ${productKey}:`, error);
      } finally {
        setLoading(false);
      }
    };

    if (productKey && isAuthenticated) {
      fetchFeatures();
    } else if (!isAuthenticated) {
      setLoading(false);
    }
  }, [productKey, refreshToken, isAuthenticated, getAccessTokenSilently]);

  const canUseFeature = (featureKey: string): boolean => {
    return features[featureKey]?.can_use ?? false;
  };

  const isProOnly = (featureKey: string): boolean => {
    return features[featureKey]?.is_pro_only ?? false;
  };

  const refresh = () => {
    setRefreshToken(prev => prev + 1);
  };

  return { features, loading, error, canUseFeature, isProOnly, refresh };
};
